import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./Blog.css";
import { blogPosts, blogPostsBySlug } from "./blogData";
import hiveText from "../../assets/logos/hivecutext.webp";
import siteBg from "../../assets/backgrounds/site_bg.png";

const getRelatedPosts = (currentPost, posts, limit = 6) => {
  const currentKeywords = new Set(
    currentPost.keywords.map((keyword) => keyword.toLowerCase()),
  );

  const scored = posts
    .filter((post) => post.slug !== currentPost.slug)
    .map((post) => {
      let score = 0;

      if (post.category === currentPost.category) {
        score += 6;
      }

      post.keywords.forEach((keyword) => {
        if (currentKeywords.has(keyword.toLowerCase())) {
          score += 2;
        }
      });

      if (
        post.title.toLowerCase().includes("seo") ||
        post.title.toLowerCase().includes("aeo")
      ) {
        score += 1;
      }

      return { post, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((item) => item.post);
};

const buildSeoExpansion = (post) => {
  return [
    {
      heading: "Intentia de cautare si pozitionarea corecta",
      paragraphs: [
        `Pentru termenul ${post.title.toLowerCase()}, utilizatorii cauta de regula doua lucruri: informatie aplicata si un furnizor de incredere. De aceea, articolul trebuie sa raspunda clar la intrebari concrete, dar sa si directioneze natural spre servicii reale. In practica, paginile care transforma cel mai bine traficul organic sunt cele care combina educatia cu dovezi de executie: exemple, procese si rezultate.`,
        "O strategie SEO buna incepe cu segmentarea intentiei: informationala, comparativa si tranzactionala. Daca toate aceste intentii sunt acoperite intr-un flux logic, pagina are sanse mai mari sa rankeze stabil si sa converteasca in lead-uri. Pentru nișa design interior si amenajari interioare, recomandarea este sa folosesti exemple locale, termeni comerciali din Romania si studii de caz relevante pentru tipul de client pe care il targetezi.",
        "Pentru AEO, structura conteaza la fel de mult ca textul. AI engine-urile extrag pasaje scurte si clare. Daca paragrafele au idei compacte si heading-urile sunt descriptive, creste probabilitatea ca brandul tau sa fie citat in raspunsuri generate.",
      ],
    },
    {
      heading: "Structura recomandata pentru pagini care rankeaza",
      paragraphs: [
        "O pagina de servicii performanta are o ierarhie simpla: problema clientului, solutia oferita, metoda de lucru, dovada sociala, intrebari frecvente si call to action. Aceasta structura reduce frictiunea si ajuta algoritmii sa inteleaga subiectul principal al paginii.",
        "Includerea de date structurate de tip Organization, Service, FAQ si BlogPosting permite o interpretare semantica superioara in Google si in motoarele de raspuns AI. In plus, canonical-ul corect, viteza buna pe mobil si legaturile interne dintre blog si paginile de servicii sunt factori esentiali pentru cresterea autoritatii topice.",
        "Fiecare articol ar trebui sa trimita strategic spre pagina de contact si spre serviciul relevant. Astfel, traficul informational devine oportunitate comerciala, nu doar vizite fara impact in business.",
      ],
      bullets: [
        "Titlu clar orientat pe intentie",
        "Intro scurt cu promisiune concreta",
        "Subtitluri care raspund la intrebari reale",
        "FAQ cu intrebari in limbaj natural",
        "CTA final catre contact sau audit",
      ],
    },
    {
      heading: "Greseli frecvente care limiteaza cresterea SEO",
      paragraphs: [
        "Cea mai comuna greseala este continutul generic, scris fara diferentiere. In nisa de servicii, paginile aproape identice intre competitori nu transmit autoritate, iar Google prioritizeaza sursele cu semnale clare de expertiza. Evita formulari vagi si adauga context practic: pentru cine este serviciul, care este procesul, ce rezultate se urmaresc.",
        "A doua greseala este lipsa legaturii intre continut si oferta. Daca un articol aduce trafic, dar nu are CTA-uri relevante si link-uri catre servicii, potentialul comercial se pierde. Creeaza trasee simple: articol -> pagina serviciu -> contact.",
        "A treia greseala este ignorarea actualizarii. Continutul evergreen trebuie revizuit periodic cu exemple noi, termeni noi de cautare si imbunatatiri de structura. Paginile updatate consecvent au sanse mai mari sa ramana in top.",
      ],
    },
    {
      heading: "Plan practic de executie pe 90 de zile",
      paragraphs: [
        "In primele 30 de zile, obiectivul este baza tehnica: indexare corecta, sitemap actualizat, robots configurat, metadata coerenta si schema valida. In paralel, construiește un cluster de continut in jurul serviciilor principale: creare site-uri, branding, design interior, amenajari interioare.",
        "In zilele 31-60, optimizeaza intentia comerciala: adauga studii de caz, FAQ orientat pe conversie si lead magnete simple. Conecteaza fiecare articol de o pagina de servicii si urmareste evenimentele de contact ca sa vezi ce subiecte aduc lead-uri reale.",
        "In zilele 61-90, accelereaza autoritatea externa: mentiuni de brand, profile publice coerente, citari locale si colaborari de continut. Combinatia dintre SEO on-site solid si semnale off-site curate este cea care impinge paginile importante in partea superioara a rezultatelor.",
      ],
    },
  ];
};

const buildFaq = (post) => {
  return [
    {
      question: `Cum ma ajuta concret un articol despre ${post.category.toLowerCase()}?`,
      answer:
        "Un articol bine optimizat iti aduce trafic calificat din cautari organice si poate transforma vizitatorii in cereri de oferta daca include exemple, claritate si CTA-uri relevante.",
    },
    {
      question: "Cate cuvinte ar trebui sa aiba un articol SEO bun?",
      answer:
        "Pentru nise competitive, un articol complet are in general 1200-2000 de cuvinte, structurate pe intentie de cautare, cu subtitluri clare si sectiuni aplicate.",
    },
    {
      question: "Este suficient doar sa public articole pe blog?",
      answer:
        "Nu. Articolele trebuie conectate cu paginile de servicii, portofoliu si contact. Fara aceasta legatura, traficul poate creste, dar conversiile raman scazute.",
    },
    {
      question: "Cum ajuta AEO in plus fata de SEO clasic?",
      answer:
        "AEO mareste sansele ca brandul tau sa fie citat in raspunsuri AI prin structura semantica clara, FAQ-uri bine formulate si date structurate relevante.",
    },
    {
      question: "La cat timp trebuie actualizat continutul?",
      answer:
        "Ideal la 60-90 de zile pentru articolele importante, cu update pe exemple, termeni noi, date si optimizari de conversie.",
    },
  ];
};

const BlogArticle = () => {
  const { slug } = useParams();
  const post = blogPostsBySlug[slug];

  if (!post) {
    return (
      <main className="blog-article-page">
        <h1>Articolul nu a fost gasit</h1>
        <p>Linkul poate fi vechi sau invalid.</p>
        <Link to="/blog">Inapoi la blog</Link>
      </main>
    );
  }

  const canonicalUrl = `https://hiveteam.dev/blog/${post.slug}`;
  const pageTitle = `${post.title} | Blog HiveTeam`;
  const expandedSections = buildSeoExpansion(post);
  const faqItems = buildFaq(post);
  const relatedPosts = getRelatedPosts(post, blogPosts, 6);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${canonicalUrl}#article`,
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    author: {
      "@type": "Person",
      name: "Pradais Samuel",
      alternateName: "JustLupus",
      url: "https://hiveteam.dev/justlupus",
    },
    publisher: {
      "@type": "Organization",
      name: "HiveTeam",
      url: "https://hiveteam.dev",
      logo: {
        "@type": "ImageObject",
        url: "https://hiveteam.dev/static/media/hivebg.caecb0f3882bfa01106a.png",
      },
    },
    mainEntityOfPage: canonicalUrl,
    isPartOf: {
      "@type": "Blog",
      "@id": "https://hiveteam.dev/blog",
    },
    about: {
      "@type": "Thing",
      name: post.category,
    },
    articleSection: post.category,
    inLanguage: "ro-RO",
    url: canonicalUrl,
    keywords: post.keywords.join(", "),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://hiveteam.dev/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://hiveteam.dev/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: canonicalUrl,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const position = blogPosts.findIndex((item) => item.slug === post.slug);
  const prevPost = position > 0 ? blogPosts[position - 1] : null;
  const nextPost =
    position < blogPosts.length - 1 ? blogPosts[position + 1] : null;

  return (
    <main
      className="blog-article-page"
      style={{ "--blog-bg-image": `url(${siteBg})` }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content={post.keywords.join(", ")} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:modified_time" content={post.updatedAt} />
        <meta property="article:author" content="Pradais Samuel" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={post.description} />
        <link rel="author" href="https://hiveteam.dev/justlupus" />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <header className="blog-topbar">
        <Link to="/" className="blog-logo-link" aria-label="HiveTeam Homepage">
          <img src={hiveText} alt="HiveTeam" className="blog-logo" />
        </Link>
        <nav className="blog-top-actions" aria-label="Actiuni rapide">
          <Link to="/" className="blog-btn blog-btn-secondary">
            Site principal
          </Link>
          <Link to="/#service" className="blog-btn blog-btn-ghost">
            Servicii
          </Link>
          <Link to="/#contact" className="blog-btn blog-btn-primary">
            Contact rapid
          </Link>
        </nav>
      </header>

      <article className="blog-article-shell">
        <nav className="blog-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/blog">Blog</Link>
          <span>/</span>
          <span>{post.title}</span>
        </nav>

        <Link to="/blog" className="blog-back-link">
          Inapoi la blog
        </Link>

        <p className="blog-card-category">{post.category}</p>
        <h1>{post.title}</h1>
        <p className="blog-author-line">
          Autor: <Link to="/justlupus">Pradais Samuel (JustLupus)</Link>
        </p>
        <p className="blog-article-intro">{post.intro}</p>

        <div className="blog-card-meta">
          <span>Publicat: {post.publishedAt}</span>
          <span>Actualizat: {post.updatedAt}</span>
          <span>{post.readTime}</span>
        </div>

        {post.sections.map((section) => (
          <section key={section.heading} className="blog-article-section">
            <h2>{section.heading}</h2>
            {section.paragraphs &&
              section.paragraphs.map((paragraph, index) => (
                <p key={`${section.heading}-p-${index}`}>{paragraph}</p>
              ))}
            {section.bullets && (
              <ul>
                {section.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {expandedSections.map((section) => (
          <section
            key={`exp-${section.heading}`}
            className="blog-article-section"
          >
            <h2>{section.heading}</h2>
            {section.paragraphs.map((paragraph, index) => (
              <p key={`exp-p-${index}`}>{paragraph}</p>
            ))}
            {section.bullets && (
              <ul>
                {section.bullets.map((bullet) => (
                  <li key={`exp-b-${bullet}`}>{bullet}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <section className="blog-article-section">
          <h2>Intrebari frecvente</h2>
          {faqItems.map((item) => (
            <div key={item.question} className="blog-faq-item">
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </div>
          ))}
        </section>

        <section className="blog-article-cta">
          <h2>Vrei implementare, nu doar teorie?</h2>
          <p>
            Echipa HiveTeam construieste website-uri orientate pe conversie si
            branding pentru companii care vor crestere reala din Google si AI.
          </p>
          <div className="blog-hero-cta">
            <Link to="/#contact" className="blog-btn blog-btn-primary">
              Cere oferta personalizata
            </Link>
            <Link to="/#hivePort" className="blog-btn blog-btn-secondary">
              Vezi proiecte realizate
            </Link>
          </div>
        </section>

        <section className="blog-article-section">
          <h2>Articole similare</h2>
          <div className="blog-related-grid">
            {relatedPosts.map((related) => (
              <article key={related.slug} className="blog-related-card">
                <p className="blog-card-category">{related.category}</p>
                <h3>
                  <Link to={`/blog/${related.slug}`}>{related.title}</Link>
                </h3>
                <p>{related.description}</p>
                <Link className="blog-read-more" to={`/blog/${related.slug}`}>
                  Citeste articolul
                </Link>
              </article>
            ))}
          </div>
        </section>
      </article>

      <nav className="blog-article-nav" aria-label="Navigare articole">
        <div>
          {prevPost && (
            <Link to={`/blog/${prevPost.slug}`}>Articol anterior</Link>
          )}
        </div>
        <div>
          {nextPost && (
            <Link to={`/blog/${nextPost.slug}`}>Articol urmator</Link>
          )}
        </div>
      </nav>
    </main>
  );
};

export default BlogArticle;
